import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Modal,
  Card
} from "react-bootstrap";
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaSearch,
  FaExclamationTriangle,
  FaCode
} from "react-icons/fa";
import axios from "axios";
import { BASE_URL } from "../constants";
import { theme } from "../styles/theme";

export default function PromptTodoListAxios() {
  const [points, setPoints] = useState([]);
  const [newPoint, setNewPoint] = useState("");
  const [newErrorCode, setNewErrorCode] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [editingErrorCode, setEditingErrorCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const API_URL = `${BASE_URL}/promptgpts`;

  const axiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
      "Content-Type": "application/json",
    },
  });

  const fetchPoints = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get();
      setPoints(data);
      setError(null);
    } catch (err) {
      console.error("Fetch error:", err.response?.data || err.message);
      setError("Erreur lors du chargement des données.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPoints();
  }, []);

  const addPoint = async () => {
    if (!newPoint.trim() || !newErrorCode.trim()) {
      alert("Le champ 'Code Erreur' est obligatoire.");
      return;
    }
    try {
      const { data } = await axiosInstance.post(API_URL, {
        description: newPoint,
        error_code: newErrorCode, // ✅ fixed backend field
      });
      setPoints([data, ...points]);
      setNewPoint("");
      setNewErrorCode("");
    } catch (err) {
      console.error("Add error:", err.response?.data || err.message);
    }
  };

  const deletePoint = async (id) => {
    if (!window.confirm("Voulez-vous vraiment supprimer ce point ?")) return;
    try {
      await axiosInstance.delete(`/${id}`);
      setPoints(points.filter((p) => p.id !== id));
    } catch (err) {
      console.error("Delete error:", err.response?.data || err.message);
    }
  };

  const startEditing = (id, text, errorCode) => {
    setEditingId(id);
    setEditingText(text || '');
    setEditingErrorCode(errorCode || '');
    setShowModal(true);
  };

  const saveEditing = async () => {
    if (!editingText.trim() || !editingErrorCode.trim()) {
      alert("Tous les champs sont obligatoires.");
      return;
    }
    
    try {
      if (editingId) {
        // Mise à jour d'un prompt existant
        const { data } = await axiosInstance.put(`/${editingId}`, {
          description: editingText,
          error_code: editingErrorCode,
        });
        setPoints(points.map((p) => (p.id === editingId ? data : p)));
      } else {
        // Création d'un nouveau prompt
        const { data } = await axiosInstance.post(API_URL, {
          description: editingText,
          error_code: editingErrorCode,
        });
        setPoints([data, ...points]);
      }
      
      setShowModal(false);
      setEditingId(null);
      setEditingText("");
      setEditingErrorCode("");
    } catch (err) {
      console.error("Save error:", err.response?.data || err.message);
      setError("Une erreur est survenue lors de l'enregistrement.");
    }
  };

  if (loading && points.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="mt-4">
        <FaExclamationTriangle className="me-2" />
        {error}
      </Alert>
    );
  }

  return (
    <div className="prompts-list">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Gestion des prompts</h2>
        <Button 
          variant="primary" 
          onClick={() => setEditingId(null) & setShowModal(true)}
          className="d-flex align-items-center"
        >
          <FaPlus className="me-2" />
          Nouveau prompt
        </Button>
      </div>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body className="p-3">
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Rechercher un prompt..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Card.Body>
      </Card>

      <div className="table-responsive">
        <Table hover className="align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: '5%' }}>#</th>
              <th style={{ width: '25%' }}>Code Erreur</th>
              <th style={{ width: '55%' }}>Description</th>
              <th style={{ width: '15%' }} className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {points.length > 0 ? (
              points
                .filter(point => {
                  const searchLower = searchTerm.toLowerCase();
                  return (
                    (point.error_code?.toLowerCase() || '').includes(searchLower) ||
                    (point.description?.toLowerCase() || '').includes(searchLower)
                  );
                })
                .map((point, index) => (
                <tr key={point.id}>
                  <td>{index + 1}</td>
                  <td className="fw-semibold">
                    <div className="d-flex align-items-center">
                      <FaCode className="me-2" style={{ color: theme.colors.primary }} />
                      {point.error_code}
                    </div>
                  </td>
                  <td>
                    {point.description || (
                      <span className="text-muted fst-italic">Aucune description</span>
                    )}
                  </td>
                  <td className="text-end">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => startEditing(point.id, point.description, point.error_code)}
                      title="Modifier"
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => deletePoint(point.id)}
                      title="Supprimer"
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center py-4 text-muted">
                  Aucun prompt trouvé
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Form onSubmit={(e) => { e.preventDefault(); saveEditing(); }}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FaCode className="me-2" />
              {editingId ? 'Modifier le prompt' : 'Nouveau prompt'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Code Erreur <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                className="text-uppercase"
                value={editingErrorCode}
                onChange={(e) => setEditingErrorCode(e.target.value)}
                placeholder="Ex: ERROR_404"
                required
              />
            </Form.Group>
            
            <Form.Group>
              <Form.Label>Description <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={editingText}
                onChange={(e) => setEditingText(e.target.value)}
                placeholder="Saisissez la description du prompt..."
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={!editingText.trim() || !editingErrorCode.trim()}
            >
              {editingId ? 'Mettre à jour' : 'Créer'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
